import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Fade,
  Alert,
  Drawer,
  AppBar,
  Toolbar,
  Paper,
  Collapse,
  alpha,
  useTheme,
  useMediaQuery,
  keyframes,
  Fab,
  Tooltip,
  Badge,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useGlobalChatContext } from '../contexts/GlobalChatContext';
import { GlobalChatMessage } from '../types/globalChat';
import { brand, gray } from '../theme/themePrimitives';
import MarkdownMessage from './MarkdownMessage';

// Typing indicator animation - smooth wave effect
const typingDot = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1.1);
  }
`;

// Pulse animation for FAB when no conversation
const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 ${alpha(brand[400], 0.5)}; }
  70% { box-shadow: 0 0 0 12px ${alpha(brand[400], 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(brand[400], 0)}; }
`;

// Floating entrance animation - refined easing
const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Subtle shimmer animation for AI avatar
const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

// Message appear animation
const messageAppear = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Swipe indicator bounce
const swipeBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
`;

// Gradient shift for header
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

interface GlobalChatProps {
  /** Position on screen for FAB and panel (default: bottom-right) */
  position?: 'bottom-right' | 'bottom-left';
  /** Z-index for the chat components (default: 2100) */
  zIndex?: number;
}

/**
 * GlobalChat - AI-powered chat assistant available throughout the application
 *
 * Displays as a floating action button (FAB) that expands into a chat panel.
 * On mobile, the chat opens as a full-screen drawer for better usability.
 * On desktop, it appears as an elegant floating panel in the corner.
 */
const GlobalChat: React.FC<GlobalChatProps> = ({
  position = 'bottom-right',
  zIndex = 2100,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state,
    isLoading,
    isSending,
    error,
    closeChat,
    toggleChat,
    startConversation,
    sendMessage,
    endConversation,
    clearError,
    hideFab,
  } = useGlobalChatContext();

  const [inputValue, setInputValue] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const touchStartY = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Position styles based on prop
  const positionStyles = position === 'bottom-right'
    ? { right: { xs: 16, sm: 24 }, left: 'auto' }
    : { left: { xs: 16, sm: 24 }, right: 'auto' };

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [state.messages, scrollToBottom]);

  // Focus input when chat opens
  useEffect(() => {
    if (state.isOpen && !isMobile && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [state.isOpen, isMobile, isMinimized]);

  // Mobile swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    if (diff > 0) {
      setSwipeOffset(diff);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (swipeOffset > 100) {
      closeChat();
    }
    setSwipeOffset(0);
  }, [swipeOffset, closeChat]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending || isLoading) return;

    const message = inputValue.trim();
    setInputValue('');

    // If no active conversation, start one with this message
    if (!state.hasActiveConversation) {
      await startConversation(message);
    } else {
      await sendMessage(message);
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = async () => {
    if (state.hasActiveConversation) {
      await endConversation();
    }
  };

  const handleToggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  // Render a single message bubble
  const renderMessage = (message: GlobalChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const isDark = theme.palette.mode === 'dark';

    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          mb: 2.5,
          px: { xs: 2, sm: 2.5 },
          animation: `${messageAppear} 0.35s cubic-bezier(0.22, 1, 0.36, 1)`,
          animationDelay: `${Math.min(index * 40, 160)}ms`,
          animationFillMode: 'both',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            maxWidth: { xs: '85%', sm: '78%' },
            flexDirection: isUser ? 'row-reverse' : 'row',
          }}
        >
          {/* Avatar with gradient for AI */}
          <Avatar
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              boxShadow: `0 2px 8px ${alpha(
                isUser ? theme.palette.primary.main : brand[400],
                0.25
              )}`,
              ...(isUser
                ? {
                    background: `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
                  }
                : {
                    background: isDark
                      ? `linear-gradient(145deg, ${brand[600]} 0%, ${brand[700]} 100%)`
                      : `linear-gradient(145deg, ${brand[300]} 0%, ${brand[400]} 100%)`,
                    // Shimmer effect for AI
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      borderRadius: '50%',
                      background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.2)}, transparent)`,
                      backgroundSize: '200% 100%',
                      animation: `${shimmer} 2.5s infinite`,
                    },
                  }),
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {isUser ? (
              <PersonIcon sx={{ fontSize: 18, color: brand[50] }} />
            ) : (
              <AutoAwesomeIcon sx={{ fontSize: 18, color: brand[50] }} />
            )}
          </Avatar>

          {/* Message bubble with glassmorphism for AI */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: isUser
                ? '18px 18px 6px 18px'
                : '18px 18px 18px 6px',
              p: { xs: 1.75, sm: 2 },
              transition: 'all 0.2s ease',
              ...(isUser
                ? {
                    background: `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
                    color: brand[50],
                    boxShadow: `0 4px 16px ${alpha(brand[400], 0.3)}, 0 2px 4px ${alpha(brand[500], 0.2)}`,
                  }
                : {
                    // Glassmorphism effect for AI messages
                    background: isDark
                      ? alpha(gray[800], 0.85)
                      : alpha('#fff', 0.9),
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: `1px solid ${alpha(
                      isDark ? gray[600] : gray[200],
                      isDark ? 0.3 : 0.6
                    )}`,
                    boxShadow: isDark
                      ? `0 4px 24px ${alpha('#000', 0.25)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
                      : `0 4px 24px ${alpha(brand[400], 0.08)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
                  }),
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: isUser
                  ? `0 6px 20px ${alpha(brand[400], 0.35)}, 0 3px 6px ${alpha(brand[500], 0.25)}`
                  : isDark
                    ? `0 6px 28px ${alpha('#000', 0.3)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
                    : `0 6px 28px ${alpha(brand[400], 0.12)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
              },
            }}
          >
            {isUser ? (
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.6,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontSize: { xs: '0.875rem', sm: '0.9rem' },
                  letterSpacing: '0.01em',
                  color: brand[50],
                }}
              >
                {message.content}
              </Typography>
            ) : (
              <MarkdownMessage content={message.content} />
            )}

            <Typography
              variant="caption"
              sx={{
                opacity: isUser ? 0.75 : 0.55,
                fontSize: '0.68rem',
                display: 'block',
                mt: 0.75,
                textAlign: isUser ? 'right' : 'left',
                fontWeight: 500,
                letterSpacing: '0.02em',
              }}
            >
              {formatTime(message.createdAt)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Typing indicator with polished animations
  const renderTypingIndicator = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Fade in timeout={400}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1.5,
            mb: 2.5,
            px: { xs: 2, sm: 2.5 },
            animation: `${messageAppear} 0.35s cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: isDark
                ? `linear-gradient(145deg, ${brand[600]} 0%, ${brand[700]} 100%)`
                : `linear-gradient(145deg, ${brand[300]} 0%, ${brand[400]} 100%)`,
              boxShadow: `0 2px 8px ${alpha(brand[400], 0.25)}`,
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 18, color: brand[50] }} />
          </Avatar>
          <Box
            sx={{
              p: 1.75,
              px: 2.5,
              borderRadius: '18px 18px 18px 6px',
              background: isDark
                ? alpha(gray[800], 0.85)
                : alpha('#fff', 0.9),
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid ${alpha(
                isDark ? gray[600] : gray[200],
                isDark ? 0.3 : 0.6
              )}`,
              display: 'flex',
              gap: 0.75,
              alignItems: 'center',
              boxShadow: isDark
                ? `0 4px 24px ${alpha('#000', 0.25)}`
                : `0 4px 24px ${alpha(brand[400], 0.08)}`,
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: brand[400],
                  animation: `${typingDot} 1.2s ease-in-out infinite`,
                  animationDelay: `${i * 0.15}s`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Fade>
    );
  };

  // Welcome state when chat is open but no messages
  const renderWelcomeState = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          flex: 1,
          animation: `${floatIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {/* Animated AI avatar with glow */}
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
            position: 'relative',
            boxShadow: `0 8px 32px ${alpha(brand[400], 0.3)}`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -3,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${alpha(brand[300], 0.4)}, ${alpha(brand[500], 0.1)})`,
              zIndex: -1,
            },
          }}
        >
          <AutoAwesomeIcon
            sx={{
              fontSize: 36,
              color: brand[50],
            }}
          />
        </Box>

        <Typography
          variant="h6"
          fontWeight="700"
          sx={{
            mb: 1,
            background: isDark
              ? `linear-gradient(135deg, ${gray[100]} 0%, ${gray[300]} 100%)`
              : `linear-gradient(135deg, ${gray[800]} 0%, ${gray[600]} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          CribCritic Assistant
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 280,
            mb: 3,
            lineHeight: 1.6,
            px: 1,
          }}
        >
          Hi there! I&apos;m your AI-powered property assistant. Ask me anything about homes, neighborhoods, or your search.
        </Typography>

        {/* Suggestion chips */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            maxWidth: 320,
          }}
        >
          {['Find my dream home', 'Explain buying steps', 'Compare neighborhoods'].map((suggestion, i) => (
            <Box
              key={suggestion}
              onClick={() => {
                setInputValue(suggestion);
                inputRef.current?.focus();
              }}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 3,
                bgcolor: isDark
                  ? alpha(brand[400], 0.15)
                  : alpha(brand[400], 0.08),
                color: isDark ? brand[200] : brand[600],
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: `1px solid ${alpha(brand[400], isDark ? 0.2 : 0.15)}`,
                animation: `${messageAppear} 0.4s cubic-bezier(0.22, 1, 0.36, 1)`,
                animationDelay: `${0.2 + i * 0.1}s`,
                animationFillMode: 'both',
                '&:hover': {
                  bgcolor: isDark
                    ? alpha(brand[400], 0.25)
                    : alpha(brand[400], 0.15),
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(brand[400], 0.2)}`,
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {suggestion}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Error display with refined styling
  const renderError = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Collapse in={!!error}>
        <Alert
          severity="error"
          onClose={clearError}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => clearError()}
              sx={{
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'rotate(180deg)' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
          sx={{
            mx: 2,
            mt: 1.5,
            borderRadius: 3,
            backdropFilter: 'blur(8px)',
            bgcolor: isDark
              ? alpha(theme.palette.error.dark, 0.2)
              : alpha(theme.palette.error.light, 0.15),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: theme.palette.error.main,
            },
          }}
        >
          {error}
        </Alert>
      </Collapse>
    );
  };

  // Input area with polished styling
  const renderInput = () => {
    const isDark = theme.palette.mode === 'dark';
    const hasInput = inputValue.trim().length > 0;

    return (
      <Box
        sx={{
          p: 2,
          pb: isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.15 : 0.08)}`,
          bgcolor: isDark
            ? alpha(gray[900], 0.95)
            : alpha('#fff', 0.98),
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={
              state.hasActiveConversation
                ? 'Type your message...'
                : 'Ask me anything about properties...'
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: isDark
                  ? alpha(gray[800], 0.6)
                  : alpha(gray[100], 0.6),
                transition: 'all 0.25s ease',
                '& fieldset': {
                  borderColor: 'transparent',
                  transition: 'all 0.25s ease',
                },
                '&:hover': {
                  bgcolor: isDark
                    ? alpha(gray[800], 0.8)
                    : alpha(gray[100], 0.8),
                  '& fieldset': {
                    borderColor: alpha(brand[400], 0.3),
                  },
                },
                '&.Mui-focused': {
                  bgcolor: isDark
                    ? alpha(gray[800], 0.9)
                    : alpha('#fff', 1),
                  boxShadow: `0 0 0 3px ${alpha(brand[400], 0.15)}`,
                  '& fieldset': {
                    borderColor: brand[400],
                    borderWidth: '1.5px',
                  },
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '0.9rem',
                lineHeight: 1.5,
                '&::placeholder': {
                  opacity: 0.6,
                },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!hasInput || isLoading || isSending}
            aria-label="Send message"
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              background: hasInput && !isLoading && !isSending
                ? `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`
                : isDark
                  ? alpha(gray[700], 0.5)
                  : alpha(gray[300], 0.5),
              color: hasInput && !isLoading && !isSending
                ? brand[50]
                : isDark
                  ? gray[500]
                  : gray[400],
              boxShadow: hasInput && !isLoading && !isSending
                ? `0 4px 12px ${alpha(brand[400], 0.3)}`
                : 'none',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: hasInput && !isLoading && !isSending
                  ? `linear-gradient(145deg, ${brand[500]} 0%, ${brand[600]} 100%)`
                  : isDark
                    ? alpha(gray[600], 0.5)
                    : alpha(gray[300], 0.6),
                transform: hasInput ? 'scale(1.05)' : 'none',
                boxShadow: hasInput && !isLoading && !isSending
                  ? `0 6px 16px ${alpha(brand[400], 0.4)}`
                  : 'none',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '&:disabled': {
                background: isDark
                  ? alpha(gray[700], 0.3)
                  : alpha(gray[300], 0.4),
              },
            }}
          >
            {isSending ? (
              <CircularProgress size={20} sx={{ color: 'inherit' }} />
            ) : (
              <SendIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Box>
      </Box>
    );
  };

  // Mobile header with gradient and swipe indicator
  const renderMobileHeader = () => (
    <AppBar
      position="static"
      elevation={0}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        paddingTop: 'env(safe-area-inset-top)',
        borderRadius: 0,
        background: `linear-gradient(135deg, ${brand[500]} 0%, ${brand[400]} 50%, ${brand[500]} 100%)`,
        backgroundSize: '200% 200%',
        animation: isSending ? `${gradientShift} 3s ease infinite` : 'none',
      }}
    >
      {/* Swipe indicator */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: alpha('#fff', 0.3),
            animation: `${swipeBounce} 2s ease-in-out infinite`,
          }}
        />
      </Box>

      <Toolbar sx={{ minHeight: 52, gap: 1.5, pb: 1 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: alpha('#fff', 0.2),
            backdropFilter: 'blur(4px)',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 20, color: brand[50] }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="700"
            sx={{ color: brand[50], letterSpacing: '-0.01em' }}
          >
            CribCritic Assistant
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: alpha(brand[50], 0.85),
              fontWeight: 500,
            }}
          >
            {isSending
              ? 'Thinking...'
              : state.hasActiveConversation
                ? `${state.messages.length} messages`
                : 'Start a conversation'}
          </Typography>
        </Box>
        {state.hasActiveConversation && (
          <Tooltip title="New conversation">
            <span>
              <IconButton
                onClick={handleNewConversation}
                disabled={isLoading}
                size="small"
                aria-label="New conversation"
                sx={{
                  color: brand[50],
                  bgcolor: alpha('#fff', 0.1),
                  '&:hover': { bgcolor: alpha('#fff', 0.2) },
                  '&.Mui-disabled': {
                    color: alpha(brand[50], 0.5),
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          onClick={closeChat}
          edge="end"
          sx={{
            color: brand[50],
            bgcolor: alpha('#fff', 0.1),
            '&:hover': { bgcolor: alpha('#fff', 0.2) },
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  // Desktop header with refined styling
  const renderDesktopHeader = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.15 : 0.08)}`,
          bgcolor: isDark
            ? alpha(gray[900], 0.95)
            : alpha('#fff', 0.98),
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            background: `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
            boxShadow: `0 4px 12px ${alpha(brand[400], 0.25)}`,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 22, color: brand[50] }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            fontWeight="700"
            sx={{ letterSpacing: '-0.01em' }}
          >
            CribCritic Assistant
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* Status indicator */}
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: isSending
                  ? 'warning.main'
                  : 'success.main',
                boxShadow: isSending
                  ? `0 0 8px ${theme.palette.warning.main}`
                  : `0 0 8px ${theme.palette.success.main}`,
                animation: isSending
                  ? `${typingDot} 1s ease-in-out infinite`
                  : 'none',
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {isSending
                ? 'Thinking...'
                : state.hasActiveConversation
                  ? 'Online'
                  : 'Ready to help'}
            </Typography>
          </Box>
        </Box>

        {/* Action buttons with refined styling */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {state.hasActiveConversation && (
            <Tooltip title="New conversation" arrow>
              <span>
                <IconButton
                  onClick={handleNewConversation}
                  disabled={isLoading || isSending}
                  size="small"
                  aria-label="New conversation"
                  sx={{
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isMinimized ? 'Expand' : 'Minimize'} arrow>
            <IconButton
              onClick={handleToggleMinimize}
              size="small"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              {isMinimized ? (
                <OpenInFullIcon sx={{ fontSize: 18 }} />
              ) : (
                <MinimizeIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close" arrow>
            <IconButton
              onClick={closeChat}
              size="small"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.action.hover, 0.8),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  // Desktop chat panel with glassmorphism and refined shadows
  const renderDesktopPanel = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Fade in={state.isOpen} timeout={300}>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 92 },
            ...positionStyles,
            width: { sm: 380, md: 400 },
            height: isMinimized ? 'auto' : { sm: 520, md: 560 },
            maxHeight: 'calc(100vh - 130px)',
            borderRadius: 5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            zIndex,
            animation: `${floatIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1)`,
            bgcolor: isDark
              ? alpha(gray[900], 0.92)
              : alpha('#fff', 0.95),
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(
              isDark ? gray[700] : gray[200],
              isDark ? 0.3 : 0.5
            )}`,
            boxShadow: isDark
              ? `0 24px 80px ${alpha('#000', 0.5)}, 0 8px 24px ${alpha('#000', 0.3)}, inset 0 1px 0 ${alpha('#fff', 0.05)}`
              : `0 24px 80px ${alpha(brand[400], 0.12)}, 0 8px 24px ${alpha(gray[400], 0.15)}, inset 0 1px 0 ${alpha('#fff', 0.8)}`,
          }}
        >
          {renderDesktopHeader()}

          <Collapse in={!isMinimized} timeout={300}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: { sm: 'calc(520px - 72px)', md: 'calc(560px - 72px)' },
              }}
            >
              {renderError()}

              {/* Messages area with subtle gradient background */}
              <Box
                aria-live="polite"
                aria-busy={isLoading || isSending}
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  py: 2,
                  background: isDark
                    ? `linear-gradient(180deg, ${alpha(gray[900], 0.3)} 0%, ${alpha(gray[800], 0.2)} 100%)`
                    : `linear-gradient(180deg, ${alpha(gray[50], 0.5)} 0%, ${alpha(gray[100], 0.3)} 100%)`,
                  display: 'flex',
                  flexDirection: 'column',
                  // Custom scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: alpha(gray[400], 0.3),
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: alpha(gray[400], 0.5),
                    },
                  },
                }}
              >
                {state.messages.length === 0 && !isLoading ? (
                  renderWelcomeState()
                ) : (
                  <>
                    {state.messages.map((message, index) =>
                      renderMessage(message, index)
                    )}
                    {(isSending || isLoading) && renderTypingIndicator()}
                  </>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {renderInput()}
            </Box>
          </Collapse>
        </Paper>
      </Fade>
    );
  };

  // Mobile drawer content with polished styling
  const renderMobileContent = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transform: `translateY(${swipeOffset}px)`,
          transition: swipeOffset ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: Math.max(1 - swipeOffset / 200, 0.6),
          bgcolor: isDark ? gray[900] : 'background.default',
        }}
      >
        {renderMobileHeader()}
        {renderError()}

        {/* Messages area with gradient background */}
        <Box
          aria-live="polite"
          aria-busy={isLoading || isSending}
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 2,
            background: isDark
              ? `linear-gradient(180deg, ${alpha(gray[900], 0.5)} 0%, ${alpha(gray[800], 0.3)} 100%)`
              : `linear-gradient(180deg, ${alpha(gray[50], 0.6)} 0%, ${alpha(gray[100], 0.4)} 100%)`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {state.messages.length === 0 && !isLoading ? (
            renderWelcomeState()
          ) : (
            <>
              {state.messages.map((message, index) =>
                renderMessage(message, index)
              )}
              {(isSending || isLoading) && renderTypingIndicator()}
            </>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {renderInput()}
      </Box>
    );
  };

  // Floating Action Button (FAB) with refined styling
  const renderFAB = () => {
    const isDark = theme.palette.mode === 'dark';

    return (
      <Tooltip
        title={state.isOpen ? 'Close chat' : 'Chat with AI assistant'}
        placement="left"
        arrow
      >
        <Badge
          color="secondary"
          variant="dot"
          invisible={!state.hasActiveConversation || state.isOpen}
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{
            position: 'fixed',
            bottom: { xs: 20, sm: 28 },
            ...positionStyles,
            zIndex: zIndex + 1,
            '& .MuiBadge-badge': {
              width: 14,
              height: 14,
              borderRadius: '50%',
              border: `2px solid ${isDark ? gray[900] : '#fff'}`,
              boxShadow: `0 0 8px ${theme.palette.secondary.main}`,
            },
          }}
        >
          <Fab
            onClick={toggleChat}
            aria-label={state.isOpen ? 'Close chat' : 'Open chat'}
            sx={{
              width: { xs: 60, sm: 64 },
              height: { xs: 60, sm: 64 },
              background: state.isOpen
                ? `linear-gradient(145deg, ${brand[600]} 0%, ${brand[500]} 100%)`
                : `linear-gradient(145deg, ${brand[400]} 0%, ${brand[500]} 100%)`,
              boxShadow: `0 8px 32px ${alpha(brand[400], 0.4)}, 0 4px 12px ${alpha(brand[500], 0.3)}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: !state.hasActiveConversation && !state.isOpen
                ? `${pulseAnimation} 2.5s ease-in-out infinite`
                : 'none',
              '&:hover': {
                transform: state.isOpen ? 'rotate(90deg)' : 'scale(1.08)',
                background: `linear-gradient(145deg, ${brand[500]} 0%, ${brand[600]} 100%)`,
                boxShadow: `0 12px 40px ${alpha(brand[400], 0.5)}, 0 6px 16px ${alpha(brand[500], 0.4)}`,
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            {state.isOpen ? (
              <CloseIcon sx={{ fontSize: { xs: 26, sm: 30 }, color: brand[50] }} />
            ) : (
              <ChatIcon sx={{ fontSize: { xs: 26, sm: 30 }, color: brand[50] }} />
            )}
          </Fab>
        </Badge>
      </Tooltip>
    );
  };

  return (
    <>
      {/* FAB - hidden on mobile when drawer is open (drawer has its own close controls), or when hideFab is set (e.g., during full-screen dialogs) */}
      {!hideFab && !(isMobile && state.isOpen) && renderFAB()}

      {/* Desktop: Floating panel */}
      {!isMobile && state.isOpen && renderDesktopPanel()}

      {/* Mobile: Full-screen drawer with refined styling */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={state.isOpen}
          onClose={closeChat}
          sx={{
            zIndex,
            '& .MuiDrawer-paper': {
              height: '100%',
              maxHeight: '100%',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              overflow: 'hidden',
              boxShadow: `0 -8px 40px ${alpha(brand[400], 0.15)}`,
            },
            '& .MuiBackdrop-root': {
              bgcolor: alpha('#000', 0.5),
              backdropFilter: 'blur(4px)',
            },
          }}
          SlideProps={{
            direction: 'up',
          }}
          transitionDuration={350}
        >
          {renderMobileContent()}
        </Drawer>
      )}
    </>
  );
};

export default GlobalChat;
